import React, { useState, useEffect } from 'react';
import { useDogs } from '../hooks/useDogs';
import { useBathPackages } from '../hooks/useBathPackages';
import { calculateSizeTag, formatDogAge } from '../utils/dogUtils';
import { GROOMING_SERVICES } from '../utils/groomingUtils';

const GroomingBookingForm = ({ onSubmit, onCancel, loading, editingBooking = null }) => {
    const { dogs, loading: dogsLoading, loadDogs } = useDogs();
    const bathPkgHook = useBathPackages();

    const [dogMode, setDogMode] = useState('select'); // 'select' | 'manual'
    const [activeBathPackages, setActiveBathPackages] = useState([]);
    const [selectedBathPackageId, setSelectedBathPackageId] = useState('');

    const [formData, setFormData] = useState({
        dogId: '',
        manualDogName: '',
        manualOwnerName: '',
        ownerPhone: '',
        weight: '',
        notes: '',
        appointmentDate: '',
        departureTime: '',
        groomingServices: ['bath'],
        status: 'scheduled',
        paymentStatus: 'pending'
    });

    const [errors, setErrors] = useState({});
    const [selectedDog, setSelectedDog] = useState(null);
    const [dogSearch, setDogSearch] = useState('');

    // A booking without a dog (manual entry) can still be pointed at a real dog
    // while editing — that is how a walk-in gets attached to its profile later.
    const canPickDog = !editingBooking || !editingBooking.dog;

    // Search dogs against the full dataset (not just the first page) as the user types
    useEffect(() => {
        const timer = setTimeout(() => {
            loadDogs({ search: dogSearch, limit: 50 });
        }, 300);
        return () => clearTimeout(timer);
    }, [dogSearch, loadDogs]);

    // Pre-fill form when editing
    useEffect(() => {
        if (editingBooking) {
            const appointmentDate = new Date(editingBooking.appointmentDate);
            const pad = n => String(n).padStart(2, '0');
            const formattedDate = `${appointmentDate.getFullYear()}-${pad(appointmentDate.getMonth() + 1)}-${pad(appointmentDate.getDate())}T${pad(appointmentDate.getHours())}:${pad(appointmentDate.getMinutes())}`;

            const isManual = !editingBooking.dog && editingBooking.manualDogName;
            setDogMode(isManual ? 'manual' : 'select');

            setFormData({
                dogId: editingBooking.dog?._id || '',
                manualDogName: editingBooking.manualDogName || '',
                manualOwnerName: editingBooking.manualOwnerName || '',
                ownerPhone: editingBooking.ownerPhone || '',
                weight: editingBooking.weight || '',
                notes: editingBooking.notes || '',
                appointmentDate: formattedDate,
                departureTime: editingBooking.departureTime || '',
                groomingServices: editingBooking.groomingServices || ['full-grooming'],
                status: editingBooking.status || 'scheduled',
                paymentStatus: editingBooking.paymentStatus || 'pending'
            });

            if (editingBooking.dog) {
                setSelectedDog(editingBooking.dog);
            }
        }
    }, [editingBooking]);

    // Auto-fill owner phone, weight and fetch bath packages when dog selected
    useEffect(() => {
        if (formData.dogId && dogs.length > 0) {
            const dog = dogs.find(d => d._id === formData.dogId);
            setSelectedDog(dog);

            if (dog) {
                const updates = {};
                if (dog.owners && dog.owners.length > 0 && dog.owners[0].phone) {
                    updates.ownerPhone = dog.owners[0].phone;
                }
                if (dog.weight) {
                    updates.weight = dog.weight.toString();
                }
                if (Object.keys(updates).length > 0) {
                    setFormData(prev => ({ ...prev, ...updates }));
                }

                // Fetch active bath packages for this dog
                bathPkgHook.fetchForDog(dog._id).then(pkgs => {
                    const active = pkgs.filter(p => p.status === 'active');
                    setActiveBathPackages(active);
                    if (active.length > 0) setSelectedBathPackageId(active[0]._id);
                    else { setActiveBathPackages([]); setSelectedBathPackageId(''); }
                });
            }
        } else if (!formData.dogId && dogMode === 'select') {
            setSelectedDog(null);
            setActiveBathPackages([]);
            setSelectedBathPackageId('');
        }
    }, [formData.dogId, dogs, dogMode]);

    const handleInputChange = (e) => {
        const { name, value, type } = e.target;

        if (type === 'checkbox') {
            const { checked } = e.target;
            setFormData(prev => ({
                ...prev,
                groomingServices: checked
                    ? [...prev.groomingServices, value]
                    : prev.groomingServices.filter(s => s !== value)
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleModeSwitch = (mode) => {
        setDogMode(mode);
        setErrors({});
        if (mode === 'select') {
            setFormData(prev => ({ ...prev, manualDogName: '', manualOwnerName: '' }));
        } else {
            setFormData(prev => ({ ...prev, dogId: '', ownerPhone: '', weight: '' }));
            setSelectedDog(null);
            setDogSearch('');
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (dogMode === 'select') {
            if (!formData.dogId) {
                newErrors.dogId = 'Selecciona un perro';
            }
        } else {
            if (!formData.manualDogName.trim()) {
                newErrors.manualDogName = 'Ingresa el nombre del perro';
            }
            if (!formData.ownerPhone.trim()) {
                newErrors.ownerPhone = 'El teléfono del propietario es requerido';
            }
        }

        if (!formData.appointmentDate) {
            newErrors.appointmentDate = 'La fecha y hora de la cita es requerida';
        } else if (!editingBooking) {
            const appointmentDate = new Date(formData.appointmentDate);
            const appointmentDateOnly = new Date(appointmentDate);
            appointmentDateOnly.setHours(0, 0, 0, 0);
            const todayOnly = new Date();
            todayOnly.setHours(0, 0, 0, 0);
            if (appointmentDateOnly < todayOnly) {
                newErrors.appointmentDate = 'La fecha de la cita no puede ser en el pasado';
            }
        }

        if (formData.groomingServices.length === 0) {
            newErrors.groomingServices = 'Selecciona al menos un servicio de estética';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const submissionData = {
            appointmentDate: formData.appointmentDate ? new Date(formData.appointmentDate).toISOString() : formData.appointmentDate,
            departureTime: formData.departureTime || '',
            groomingServices: formData.groomingServices,
            ownerPhone: formData.ownerPhone,
            weight: formData.weight ? parseFloat(formData.weight) : undefined,
            notes: formData.notes,
            paymentStatus: formData.paymentStatus,
            ...(editingBooking && { status: formData.status }),
            ...(formData.paymentStatus === 'bath-package' && selectedBathPackageId && { bathPackageId: selectedBathPackageId }),
        };

        if (dogMode === 'select') {
            submissionData.dogId = formData.dogId;
        } else {
            submissionData.manualDogName = formData.manualDogName.trim();
            submissionData.manualOwnerName = formData.manualOwnerName.trim();
        }

        onSubmit(submissionData);
    };

    const resetForm = () => {
        setFormData({
            dogId: '',
            manualDogName: '',
            manualOwnerName: '',
            ownerPhone: '',
            weight: '',
            notes: '',
            appointmentDate: '',
            departureTime: '',
            groomingServices: ['bath'],
            status: 'scheduled',
            paymentStatus: 'pending'
        });
        setErrors({});
        setSelectedDog(null);
        setDogMode('select');
        setDogSearch('');
    };

    const handleCancel = () => {
        resetForm();
        onCancel();
    };

    return (
        <div className="grooming-booking-form">
            <h3>{editingBooking ? 'Editar Cita de Estética' : 'Nueva Cita de Estética'}</h3>
            <p className="form-description">
                {editingBooking
                    ? 'Actualizar los detalles de la cita de estética.'
                    : 'Programar una nueva cita de estética para un perro.'}
            </p>

            <form onSubmit={handleSubmit}>
                <div className="form-grid">
                    <div className="form-group full-width">
                        <label>Perro *</label>

                        {/* Mode toggle. While editing it only stays open for a manual
                            booking, so it can be linked to a real dog later; a booking
                            already tied to a dog keeps that dog. */}
                        {canPickDog && (
                            <div className="dog-mode-toggle">
                                <button
                                    type="button"
                                    className={`dog-mode-btn${dogMode === 'select' ? ' active' : ''}`}
                                    onClick={() => handleModeSwitch('select')}
                                >
                                    🐕 Seleccionar perro
                                </button>
                                <button
                                    type="button"
                                    className={`dog-mode-btn${dogMode === 'manual' ? ' active' : ''}`}
                                    onClick={() => handleModeSwitch('manual')}
                                >
                                    ✍️ Ingresar manualmente
                                </button>
                            </div>
                        )}

                        {dogMode === 'select' ? (
                            !canPickDog ? (
                                <div className="dog-picker-list dog-picker-list--disabled">
                                    {selectedDog && (
                                        <div className="dog-picker-card dog-picker-card--selected">
                                            <div className="dog-picker-avatar">
                                                {selectedDog.pictureUrl || selectedDog.picture
                                                    ? <img src={selectedDog.pictureUrl || selectedDog.picture} alt={selectedDog.name} />
                                                    : <span>{selectedDog.name?.[0]?.toUpperCase()}</span>
                                                }
                                            </div>
                                            <div className="dog-picker-info">
                                                <div className="dog-picker-name">{selectedDog.name}</div>
                                                <div className="dog-picker-breed">{selectedDog.breed}</div>
                                            </div>
                                            <span className="dog-picker-check">✓</span>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <input
                                        type="text"
                                        className="dog-picker-search"
                                        placeholder="Buscar perro..."
                                        value={dogSearch}
                                        onChange={e => setDogSearch(e.target.value)}
                                    />
                                    <div className="dog-picker-list">
                                        {dogs.map(dog => {
                                            const isSelected = formData.dogId === dog._id;
                                            return (
                                                <button
                                                    type="button"
                                                    key={dog._id}
                                                    className={`dog-picker-card${isSelected ? ' dog-picker-card--selected' : ''}`}
                                                    onClick={() => {
                                                        setFormData(prev => ({ ...prev, dogId: dog._id }));
                                                        setErrors(prev => ({ ...prev, dogId: '' }));
                                                    }}
                                                >
                                                    <div className="dog-picker-avatar">
                                                        {dog.pictureUrl || dog.picture
                                                            ? <img src={dog.pictureUrl || dog.picture} alt={dog.name} />
                                                            : <span>{dog.name?.[0]?.toUpperCase()}</span>
                                                        }
                                                    </div>
                                                    <div className="dog-picker-info">
                                                        <div className="dog-picker-name">{dog.name}</div>
                                                        <div className="dog-picker-breed">{dog.owners?.map(o => o.name).join(', ')}</div>
                                                    </div>
                                                    {isSelected && <span className="dog-picker-check">✓</span>}
                                                </button>
                                            );
                                        })}
                                        {dogs.length === 0 && (
                                            <div className="dog-picker-empty">No se encontraron perros</div>
                                        )}
                                    </div>
                                </>
                            )
                        ) : (
                            <div className="manual-dog-fields">
                                <input
                                    type="text"
                                    name="manualDogName"
                                    value={formData.manualDogName}
                                    onChange={handleInputChange}
                                    placeholder="Nombre del perro *"
                                />
                                {errors.manualDogName && <div className="error">{errors.manualDogName}</div>}
                                <input
                                    type="text"
                                    name="manualOwnerName"
                                    value={formData.manualOwnerName}
                                    onChange={handleInputChange}
                                    placeholder="Nombre del propietario"
                                    style={{ marginTop: 8 }}
                                />
                            </div>
                        )}
                        {errors.dogId && <div className="error">{errors.dogId}</div>}
                    </div>

                    <div className="form-group">
                        <label>Teléfono del propietario{dogMode === 'select' ? ' *' : ''}</label>
                        <input
                            type="tel"
                            name="ownerPhone"
                            value={formData.ownerPhone}
                            onChange={handleInputChange}
                            placeholder="Número de teléfono del propietario"
                        />
                        {errors.ownerPhone && <div className="error">{errors.ownerPhone}</div>}
                    </div>

                    <div className="form-group">
                        <label>Peso (kg)</label>
                        <input
                            type="number"
                            name="weight"
                            value={formData.weight}
                            onChange={handleInputChange}
                            step="0.1"
                            min="0.1"
                            max="100"
                            placeholder="e.g. 25.5"
                        />
                        {errors.weight && <div className="error">{errors.weight}</div>}
                    </div>

                    <div className="form-group">
                        <label>Fecha y hora de la cita *</label>
                        <input
                            type="datetime-local"
                            name="appointmentDate"
                            value={formData.appointmentDate}
                            onChange={handleInputChange}
                            required
                        />
                        {errors.appointmentDate && <div className="error">{errors.appointmentDate}</div>}
                    </div>

                    <div className="form-group">
                        <label>Hora de salida</label>
                        <input
                            type="time"
                            name="departureTime"
                            value={formData.departureTime}
                            onChange={handleInputChange}
                        />
                    </div>

                    {editingBooking && (
                        <div className="form-group">
                            <label>Estado</label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleInputChange}
                            >
                                <option value="scheduled">Programado</option>
                                <option value="in-progress">En proceso</option>
                                <option value="completed">Completado</option>
                                <option value="cancelled">Cancelado</option>
                            </select>
                        </div>
                    )}

                    <div className="form-group full-width">
                        <label>Estado de pago</label>
                        <select
                            name="paymentStatus"
                            value={formData.paymentStatus}
                            onChange={handleInputChange}
                        >
                            <option value="pending">Pendiente</option>
                            <option value="paid">Pagado</option>
                            <option value="membership">Membresía</option>
                            {(activeBathPackages.length > 0 || formData.paymentStatus === 'bath-package') && (
                                <option value="bath-package">Paquete de baños</option>
                            )}
                        </select>

                        {/* Bath package selector — shown when bath-package is chosen */}
                        {formData.paymentStatus === 'bath-package' && activeBathPackages.length > 0 && (
                            <div className="bp-picker-inline">
                                {activeBathPackages.map(pkg => {
                                    const used  = pkg.baths?.length || 0;
                                    const total = pkg.totalBaths || 5;
                                    const remaining = total - used;
                                    const purchaseFmt = pkg.purchaseDate
                                        ? new Date(pkg.purchaseDate).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })
                                        : '';
                                    const isSelected = selectedBathPackageId === pkg._id;
                                    return (
                                        <button
                                            key={pkg._id}
                                            type="button"
                                            className={`bp-pkg-option${isSelected ? ' selected' : ''}`}
                                            onClick={() => setSelectedBathPackageId(pkg._id)}
                                        >
                                            <div className="bp-pkg-option-top">
                                                <span className="bp-pkg-option-label">🛁 Paquete de {total} baños</span>
                                                {isSelected && <span className="bp-pkg-option-check">✓</span>}
                                            </div>
                                            <div className="bp-pkg-option-dots">
                                                {Array.from({ length: total }, (_, i) => (
                                                    <span key={i} className={`bp-mini-dot${i < used ? ' used' : ''}`} />
                                                ))}
                                            </div>
                                            <div className="bp-pkg-option-meta">
                                                {remaining} baño{remaining !== 1 ? 's' : ''} restante{remaining !== 1 ? 's' : ''}
                                                {purchaseFmt && ` · desde ${purchaseFmt}`}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {formData.paymentStatus === 'bath-package' && activeBathPackages.length === 0 && (
                            <p className="bp-no-pkg-warn">Este perro no tiene un paquete de baños activo.</p>
                        )}
                    </div>
                </div>

                <div className="form-group full-width">
                    <label>Servicios de estética *</label>
                    <div className="checkbox-group">
                        {GROOMING_SERVICES.map(service => (
                            <label key={service.value} className="checkbox-label">
                                <input
                                    type="checkbox"
                                    name="groomingServices"
                                    value={service.value}
                                    checked={formData.groomingServices.includes(service.value)}
                                    onChange={handleInputChange}
                                />
                                <span>{service.label}</span>
                            </label>
                        ))}
                    </div>
                    {errors.groomingServices && <div className="error">{errors.groomingServices}</div>}
                </div>

                <div className="form-group full-width">
                    <label>Notas</label>
                    <textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        placeholder="Instrucciones especiales o notas para el estilista..."
                        rows="3"
                    />
                </div>

                {/* Selected Dog Info (select mode only) */}
                {dogMode === 'select' && selectedDog && (
                    <div className="selected-dog-info">
                        <h4>📋 Información del perro</h4>
                        <div className="dog-info-grid">
                            <p><strong>Nombre:</strong> {selectedDog.name}</p>
                            <p><strong>Raza:</strong> {selectedDog.breed}</p>
                            <p><strong>Edad:</strong> {formatDogAge(selectedDog)}</p>
                            {selectedDog.weight && (() => {
                                const sizeTag = calculateSizeTag(selectedDog.weight);
                                return (
                                    <p>
                                        <strong>Size:</strong>
                                        <span className="size-tag-inline">
                                            <span className="size-tag-badge-small" title={sizeTag.description}>
                                                {sizeTag.tag}
                                            </span>
                                            <span className="size-tag-label-inline">{sizeTag.label}</span>
                                            <span className="size-tag-weight">({selectedDog.weight}kg)</span>
                                        </span>
                                    </p>
                                );
                            })()}
                            <p><strong>Propietario(s):</strong> {selectedDog.owners?.map(o => o.name).join(', ')}</p>
                        </div>
                    </div>
                )}

                <div className="form-actions">
                    <button
                        type="button"
                        onClick={handleCancel}
                        className="btn-secondary"
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn-primary"
                        disabled={loading || (dogMode === 'select' && dogsLoading)}
                    >
                        {editingBooking ? 'Actualizar Cita' : 'Programar Cita'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default GroomingBookingForm;
